import { useQuery } from '@tanstack/react-query';

import { apiService } from '@/services/api-service';

/**
 * ONE account's owner-folder documents, read live from the drive (issues #373, #402).
 *
 * Nothing is cached in D1 — every call is live — so these hooks lean on TanStack Query
 * for the caching instead. Query keys are built from PRIMITIVES only (id, joined path,
 * query string): an inline array or object literal in a key is a new reference every
 * render, which turns into a refetch loop.
 *
 * Association-scoped listing and search used to live here too. They are gone: the drive is
 * PATH-addressed now, so the portfolio column browser (`use-document-browse.ts`) answers
 * "what is in this association's folder" with the same endpoint it uses for every other
 * level, and search is portfolio-wide (`use-document-search.ts`).
 */

export interface DocumentFolder {
	id: string;
	name: string;
}

export interface DocumentFile {
	id: string;
	name: string;
	size?: number;
	modifiedAt?: string;
	/**
	 * Dropbox's `client_modified` — the uploading machine's mtime, not a server fact. Absent
	 * on plenty of files. Mirrored in `api/documents/primitives.ts`; move them together.
	 */
	createdAt?: string;
	extension: string;
	/** Which subfolder it came from. Set only in a flattened ("All") listing. */
	folder?: string;
}

export interface DocumentListing {
	associationId: string;
	associationName: string;
	path: string[];
	folders: DocumentFolder[];
	files: DocumentFile[];
	/** Present only when the listing is rooted at ONE account's owner folder (#402). */
	accountLabel?: string;
	/** Empty because no storage provider is connected, not because the folder is empty. */
	unavailable?: 'not-configured';
}

export const documentKeys = {
	all: ['documents'] as const,
};

/**
 * URL of the proxy endpoint for one file. Needs an auth header — see `fetchDocumentObjectUrl`.
 *
 * `encodeURIComponent` is MANDATORY: a Dropbox handle is `id:AbC…`, and an unescaped colon
 * (or a slash, in the path form) would split into extra path segments and route to nothing.
 * The handler decodes it back before use.
 *
 * There is no download variant. The bytes are always fetched in JS and handed to the viewer
 * as a blob — the proxy needs our `Authorization` header, which no `src`/`href` can send —
 * so the response's own `content-disposition` never reaches the browser's download logic.
 * `utils/document-download.ts` sets the filename on the synthetic anchor instead.
 */
export function documentFileUrl(fileId: string): string {
	return `/documents/file/${encodeURIComponent(fileId)}`;
}

/**
 * One ACCOUNT's owner folder (issue #402).
 *
 * Keyed on the account id, never a folder path — the server resolves the owner folder from
 * the account's association, so a caller cannot name a folder to read.
 */
export function useAccountDocuments(accountId: string, path: string, flatten = false) {
	// `flatten` is part of the key: the two shapes are different answers for the same
	// account, and sharing a cache entry would show one where the other was asked for.
	return useQuery<DocumentListing>({
		queryKey: [...documentKeys.all, 'account', accountId, path, flatten ? 'flat' : 'tree'] as const,
		queryFn: () => {
			const params = new URLSearchParams();
			if (path) params.set('path', path);
			if (flatten) params.set('flatten', '1');
			const query = params.toString();
			return apiService.fetchApi(
				`/documents/account/${accountId}${query ? `?${query}` : ''}`,
			) as Promise<DocumentListing>;
		},
		enabled: !!accountId,
		staleTime: 30_000,
		retry: false,
	});
}

/** Proxy URL for one file under an account's owner folder. See `documentFileUrl`. */
export function accountDocumentFileUrl(accountId: string, fileId: string): string {
	return `/documents/account/${accountId}/file/${encodeURIComponent(fileId)}`;
}

/** Invalidate an account's document listings after an upload. */
export const accountDocumentKey = (accountId: string) =>
	[...documentKeys.all, 'account', accountId] as const;
